import "./App.css";
import React, { useContext, useState, useEffect } from "react";
import ReactGA from "react-ga4";
import MainLayout from "./layouts/MainLayout";
import { Routes, Route, BrowserRouter, Navigate } from "react-router-dom";
import ThemeCustomization from "./themes";
import SignIn from "./pages/SignIn/SignIn";
import { UserContext } from "./context/UserContext";
import i18n from "../src/i18n/config";
import { transitions, positions, Provider as AlertProvider } from "react-alert";
import Plans from "./pages/Plans/Plans";
import Joyride, { ACTIONS, EVENTS } from "react-joyride";
import axios from "axios";
import { ADMIN_URL } from "./utils/constants";
import Logger from "./utils/Logger";
import { levels } from "./utils/Logger";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import AccountDeletion from "./pages/AccountDeletion/AccountDeletion";
import CookieConsent, {
	getCookieConsentValue,
	resetCookieConsentValue,
} from "react-cookie-consent";
import { ErrorBoundary } from "react-error-boundary";
import { Fallback } from "./components/Fallback/Fallback";
import { CreatorProvider } from "./context/CreatorContext";

// optional configuration
const options = {
	// you can also just use 'bottom center'
	position: positions.TOP_RIGHT,
	timeout: 5000,
	offset: "30px",
	// you can also just use 'scale'
	transition: transitions.SCALE,
	containerStyle: {
		zIndex: 9999,
	},
};

axios.defaults.headers.common["Accept-Language"] =
	i18n.language === "es" ||
	i18n.language.includes("es-") ||
	i18n.language.includes("-ES") ||
	i18n.language.includes("es_") ||
	i18n.language.includes("_ES") ||
	i18n.language === "ca" ||
	i18n.language === "eu" ||
	i18n.language === "gl"
		? "es"
		: "en";

// the style contains only the margin given as offset
// options contains all alert given options
// message is the alert message
// close is a function that closes the alert
const AlertTemplate = ({ style, options, message, close }) => (
	<div
		style={{
			backgroundColor: options.type === "error" ? "#eb4d4b" : "#43BD98",
			padding: "12px 20px",
			margin: 20,
			borderRadius: 4,
			color: "white",
			fontWeight: "bold",
		}}
	>
		{message}
	</div>
);

Logger("Start App", levels.INFO, {});

function App() {
	const { user, getUser } = useContext(UserContext);
	const { t } = useTranslation();
	const [run, setRun] = useState(false);
	const [onboardingStarted, setOnboardingStarted] = useState(false);
	const [onboardingFinished, setOnboardingFinished] = useState(false);
	const [goToQR, setGoToQR] = useState(false);
	const [goToNavigation, setGoToNavigation] = useState(false);
	const [goToAppearance, setGoToAppearance] = useState(false);
	const [goToPages, setGoToPages] = useState(false);
	const [goToHome, setGoToHome] = useState(false);
	const [goToPublish, setGoToPublish] = useState(false);
	const [step, setStep] = useState(0);
	const theme = useTheme();
	const isXs = useMediaQuery(theme.breakpoints.down("sm"));

	const consentGiven = () => {
		if (
			navigator.language === "es" ||
			navigator.language.includes("es-") ||
			navigator.language.includes("-ES") ||
			navigator.language.includes("es_") ||
			navigator.language.includes("_ES") ||
			navigator.language === "ca" ||
			navigator.language === "eu" ||
			navigator.language === "gl"
		) {
			var Tawk_API = Tawk_API || {},
				Tawk_LoadStart = new Date();
			(function () {
				var s1 = document.createElement("script"),
					s0 = document.getElementsByTagName("script")[0];
				s1.async = true;
				s1.src = "https://embed.tawk.to/6624c5131ec1082f04e529bf/1hrvr5nrq";
				s1.charset = "UTF-8";
				s1.setAttribute("crossorigin", "*");
				if (s0 !== null && s0.parentNode) {
					s0.parentNode.insertBefore(s1, s0);
				}
			})();
		} else {
			var Tawk_API = Tawk_API || {},
				Tawk_LoadStart = new Date();
			(function () {
				var s1 = document.createElement("script"),
					s0 = document.getElementsByTagName("script")[0];
				s1.async = true;
				s1.src = "https://embed.tawk.to/6624c5131ec1082f04e529bf/1hrvq3kue";
				s1.charset = "UTF-8";
				s1.setAttribute("crossorigin", "*");
				if (s0 !== null && s0.parentNode) {
					s0.parentNode.insertBefore(s1, s0);
				}
			})();
		}

		(function (w, d, s, l, i) {
			w[l] = w[l] || [];
			w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
			var f = d.getElementsByTagName(s)[0],
				j = d.createElement(s),
				dl = l != "dataLayer" ? "&l=" + l : "";
			j.async = true;
			j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
			if (f && f.parentNode) {
				f.parentNode.insertBefore(j, f);
			}
		})(window, document, "script", "dataLayer", "GTM-58ZM2D4V");

		const gaScript = document.createElement("script");
		gaScript.src = "https://www.googletagmanager.com/gtag/js?id=G-XTJXXMV0DX";
		gaScript.async = true;
		document.body.appendChild(gaScript);

		const gaIframe = document.createElement("script");
		gaIframe.src = "https://www.googletagmanager.com/ns.html?id=GTM-58ZM2D4V";
		gaIframe.width = "0";
		gaIframe.height = "0";
		gaIframe.style = "display: none; visibility: hidden";
		document.body.appendChild(gaIframe);

		window.dataLayer = window.dataLayer || [];
		function gtag() {
			window.dataLayer.push(arguments);
		}
		gtag("js", new Date());
		gtag("config", "G-XTJXXMV0DX");

		ReactGA.initialize("G-XTJXXMV0DX");

		// Hotjar Tracking Code for https://creator.applaby.com
		(function (h, o, t, j, a, r) {
			h.hj =
				h.hj ||
				function () {
					(h.hj.q = h.hj.q || []).push(arguments);
				};
			h._hjSettings = { hjid: 5160674, hjsv: 6 };
			a = o.getElementsByTagName("head")[0];
			r = o.createElement("script");
			r.async = 1;
			r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
			a.appendChild(r);
		})(window, document, "https://static.hotjar.com/c/hotjar-", ".js?sv=");
	};

	useEffect(() => {
		const resetedCookies = localStorage.getItem("resetedCookies");

		if (resetedCookies === null) {
			resetCookieConsentValue();
			localStorage.setItem("resetedCookies", new Date().getTime());
			window.location.reload();
		}

		const isConsentGiven = document.cookie.includes("CookieConsent=true");

		if (isConsentGiven) {
			consentGiven();
		}

		setTimeout(() => {
			const cookieConsentContainer = document.querySelector(".CookieConsent");
			if (cookieConsentContainer) {
				document.querySelector(
					".CookieConsent"
				).parentElement.style.zIndex = 999999;
			}
		}, 10);
	}, []);

	const handleDecline = () => {};

	const [steps] = useState([
		{
			target: "#code" + (isXs ? "-mobile" : ""),
			content: (
				<>
					{t("onboarding.code1")}
					<br />
					<br />
					{t("onboarding.code2")}
				</>
			),
			disableBeacon: true,
		},
		{
			target: "#qr",
			content: <>{t("onboarding.qr")}</>,
		},
		{
			target: "#navigation" + (isXs ? "-mobile" : ""),
			content: <>{t("onboarding.navigation")}</>,
		},
		{
			target: "#drawer",
			content: <>{t("onboarding.drawer")}</>,
		},
		{
			target: "#navigation-save",
			content: <>{t("onboarding.navigationSave")}</>,
		},
		{
			target: "#appearance" + (isXs ? "-mobile" : ""),
			content: <>{t("onboarding.appearance")}</>,
		},
		{
			target: "#main-color",
			content: <>{t("onboarding.mainColor")}</>,
		},
		{
			target: "#appearance-save",
			content: <>{t("onboarding.appearanceSave")}</>,
		},
		{
			target: "#pages" + (isXs ? "-mobile" : ""),
			content: <>{t("onboarding.pages")}</>,
		},
		{
			target: "#pages-visible",
			content: <>{t("onboarding.visible")}</>,
		},
		{
			target: "#pages-header",
			content: <>{t("onboarding.pagesHeader")}</>,
		},
		{
			target: "#home" + (isXs ? "-mobile" : ""),
			content: <>{t("onboarding.home")}</>,
		},
		{
			target: "#home-style1",
			content: <>{t("onboarding.homeStyle")}</>,
		},
		{
			target: "#tab-publish",
			content: <>{t("onboarding.publishInfo")}</>,
		},
		{
			target: "#publish" + (isXs ? "-mobile" : ""),
			content: <>{t("onboarding.publish")}</>,
		},
		{
			target: "#publish-message",
			content: <>{t("onboarding.publishMessage")}</>,
		},
		{
			target: "#tab-admin",
			content: <>{t("onboarding.admin")}</>,
		},
	]);

	return (
		<ThemeCustomization>
			<ErrorBoundary
				FallbackComponent={Fallback}
				onReset={(details) => {
					// Reset the state of your app so the error doesn't happen again
				}}
				onError={(error, info) => {
					console.error(
						error.message + " - " + info.componentStack,
						levels.FATAL
					);
				}}
			>
				<CookieConsent
					onAccept={() => {
						consentGiven();
					}}
					buttonText={t("general.cookiesAccept")}
					style={{ zIndex: 999999, background: "#FFFFFF", color: "#000000" }}
					buttonStyle={{
						backgroundColor: "#43BD98",
						color: "#FFFFFF",
						fontSize: "13px",
						padding: "8px 18px",
						borderRadius: "4px",
						fontFamily: "Montserrat",
						fontWeight: "700",
						margin: 8,
					}}
					declineButtonStyle={{
						backgroundColor: "#ffffff",
						border: "1px solid #43BD98",
						color: "#43BD98",
						fontSize: "13px",
						padding: "8px 18px",
						borderRadius: "4px",
						fontFamily: "Montserrat",
						fontWeight: "700",
						margin: 8,
						marginRight: 4,
					}}
					overlay
					onDecline={handleDecline}
					enableDeclineButton
					declineButtonText={t("general.cookiesDecline")}
				>
					{t("general.cookies")}{" "}
					<a href={t("general.cookiesLink")} style={{ color: "#000000" }}>
						{t("general.readCookies")}
					</a>
				</CookieConsent>

				<Joyride
					steps={steps}
					callback={async (data) => {
						const { action, index, status, type } = data;

						console.log(data);

						setStep(index);

						if (type === EVENTS.TOUR_END || action === ACTIONS.SKIP) {
							setRun(false);

							console.log(index);
							setOnboardingStarted(false);
							setOnboardingFinished(true);

							try {
								await axios.post(
									ADMIN_URL + "adminUpdateOnboarding",
									{},
									{
										headers: {
											authorization: localStorage.getItem("token"),
										},
									}
								);
								await getUser();
							} catch (error) {
								console.log(error);
							}
						}
						if (!onboardingFinished) {
							if (type === EVENTS.TOOLTIP && index === 0) {
								setGoToQR(true);
							}
							if (type === EVENTS.STEP_AFTER && index === 1) {
								setRun(false);
								setGoToNavigation(true);
							}
							if (type === EVENTS.STEP_AFTER && index === 4) {
								setRun(false);
								setGoToAppearance(true);
							}
							if (type === EVENTS.STEP_AFTER && index === 7) {
								setRun(false);
								setGoToPages(true);
							}
							if (type === EVENTS.STEP_AFTER && index === 9) {
								setRun(false);
								setGoToHome(true);
							}
							if (type === EVENTS.STEP_AFTER && index === 12) {
								setRun(false);
								setGoToPublish(true);
							}
						}
					}}
					locale={{
						back: t("onboarding.back"),
						close: t("onboarding.close"),
						last: t("onboarding.last"),
						next: t("onboarding.next"),
						open: t("onboarding.open"),
						skip: t("onboarding.skip"),
					}}
					continuous
					disableCloseOnEsc
					disableOverlayClose
					hideBackButton
					hideCloseButton
					run={run && onboardingStarted}
					scrollToFirstStep={false}
					scrollOffset={0}
					disableScrolling={step < 11}
					showProgress
					showSkipButton
					styles={{
						options: {
							zIndex: 10000,
							primaryColor: "#43BD98",
							fontSize: "12px",
						},
					}}
				/>
				<AlertProvider template={AlertTemplate} {...options}>
					{user === null || (user && !user.activatedAt) ? (
						<BrowserRouter>
							<Routes>
								<Route path="/" exact element={<SignIn />}></Route>
								<Route
									path="/account-deletion"
									exact
									element={<AccountDeletion />}
								></Route>
								<Route path="*" element={<Navigate to="/" replace />} />
							</Routes>
						</BrowserRouter>
					) : user &&
					  user.email !== "testios@applaby.com" &&
					  (!user.chosenAppType ||
							!user.subscription ||
							(user.subscription &&
								!["active", "past_due", "trialing"].find(
									(subscriptionStatus) =>
										subscriptionStatus === user.subscription.status
								))) ? (
						<BrowserRouter>
							<Routes>
								<Route path="/plans" exact element={<Plans />}></Route>
								<Route
									path="/account-deletion"
									exact
									element={<AccountDeletion />}
								></Route>
								<Route path="*" element={<Navigate to="/plans" replace />} />
							</Routes>
						</BrowserRouter>
					) : (
						<CreatorProvider>
							<MainLayout
								goToQR={goToQR}
								setGoToQR={setGoToQR}
								goToNavigation={goToNavigation}
								setGoToNavigation={setGoToNavigation}
								goToAppearance={goToAppearance}
								setGoToAppearance={setGoToAppearance}
								onboardingStarted={onboardingStarted}
								setOnboardingStarted={setOnboardingStarted}
								onboardingFinished={onboardingFinished}
								setOnboardingFinished={setOnboardingFinished}
								goToPages={goToPages}
								setGoToPages={setGoToPages}
								goToHome={goToHome}
								setGoToHome={setGoToHome}
								goToPublish={goToPublish}
								setGoToPublish={setGoToPublish}
								setRun={setRun}
							/>
						</CreatorProvider>
					)}
				</AlertProvider>
			</ErrorBoundary>
		</ThemeCustomization>
	);
}

export default App;
